import type { FormData, OrderFinancialVO } from "@ifa/utils";

export type ZuuRpqOptionKey = "A" | "B" | "C" | "D";
export type ZuuRiskInvestmentProfile = {
  monthlyStableSurplus?: ZuuRpqOptionKey;
  hasSufficientEmergencyLiquidity?: ZuuRpqOptionKey;
  hasLongTermSavingsGoal?: "A" | "B";
  hasInvestedInBondsOrStocks?: ZuuRpqOptionKey;
  investmentRiskTolerance?: ZuuRpqOptionKey;
  marketDownturnReaction?: ZuuRpqOptionKey;
  acceptableInvestmentLoss?: ZuuRpqOptionKey;
};

export type ZuuRpqFieldKey = keyof ZuuRiskInvestmentProfile;
export type ZuuRpqQuestionLayout = "grid-2" | "inline-2" | "stack";

export type ZuuRpqQuestionConfig = {
  key: ZuuRpqFieldKey;
  question: string;
  label: string;
  requiredMessage: string;
  layout: ZuuRpqQuestionLayout;
  options: Array<{ key: string; label: string }>;
};

export const ZUU_RPQ_REQUIRED_FIELDS: ZuuRpqFieldKey[] = [
  "monthlyStableSurplus",
  "hasSufficientEmergencyLiquidity",
  "hasLongTermSavingsGoal",
  "hasInvestedInBondsOrStocks",
  "investmentRiskTolerance",
  "marketDownturnReaction",
  "acceptableInvestmentLoss",
];

export const ZUU_RPQ_FIELD = "rpq" satisfies keyof OrderFinancialVO;

export const ZUU_RPQ_QUESTIONS: ZuuRpqQuestionConfig[] = [
  {
    key: "monthlyStableSurplus",
    question: "monthlyStableSurplus",
    layout: "grid-2",
    label: "1. 您每月有多少穩定的盈餘？（穩定的盈餘 = 平均每月收入減平均每月支出；而此盈餘是可用作儲蓄或投資）",
    requiredMessage: "請選擇第1題：您每月有多少穩定的盈餘？",
    options: [
      { key: "A", label: "≤ $5,000" },
      { key: "B", label: "$5,001 - $10,000" },
      { key: "C", label: "$10,001 - $20,000" },
      { key: "D", label: "> $20,000" },
    ],
  },
  {
    key: "hasSufficientEmergencyLiquidity",
    question: "hasSufficientEmergencyLiquidity",
    layout: "grid-2",
    label: "2. 您是否有足夠的流動資金在緊急時應付日常的生活所需？",
    requiredMessage: "請選擇第2題：您是否有足夠的流動資金在緊急時應付日常的生活所需？",
    options: [
      { key: "A", label: "流動資金 = 或 < 3個月的生活所需" },
      { key: "B", label: "流動資金 = 4-9個月的生活所需" },
      { key: "C", label: "流動資金 = 10-17個月的生活所需" },
      { key: "D", label: "流動資金 = 或 > 18個月的生活所需" },
    ],
  },
  {
    key: "hasLongTermSavingsGoal",
    question: "hasLongTermSavingsGoal",
    layout: "inline-2",
    label: "3. 您現在有否為自己訂立長遠的儲蓄目標？",
    requiredMessage: "請選擇第3題：您現在有否為自己訂立長遠的儲蓄目標？",
    options: [
      { key: "A", label: "有" },
      { key: "B", label: "沒有" },
    ],
  },
  {
    key: "hasInvestedInBondsOrStocks",
    question: "hasInvestedInBondsOrStocks",
    layout: "stack",
    label: "4. 您曾否投資於債券或股票(可以是以基金形式投資)?",
    requiredMessage: "請選擇第4題：您曾否投資於債券或股票(可以是以基金形式投資)?",
    options: [
      { key: "A", label: "有；我在資產下跌時感到不安" },
      { key: "B", label: "沒有；但若我真的作出投資，在資產下跌時，我會感到不安" },
      { key: "C", label: "有；我在資產下跌時不會感到不安" },
      { key: "D", label: "沒有；但若我真的作出投資，在資產下跌時，我不會感到不安" },
    ],
  },
  {
    key: "investmentRiskTolerance",
    question: "investmentRiskTolerance",
    layout: "stack",
    label: "5. 以下那一項最能貼切地表達您對投資所可能面對風險的態度？",
    requiredMessage: "請選擇第5題：以下那一項最能貼切地表達您對投資所可能面對風險的態度？",
    options: [
      { key: "A", label: "我不願意承擔任何風險(不能下跌)，即使得到的回報比通脹還要低" },
      { key: "B", label: "我願意承擔少許風險(可能下跌20%)，來換取比通脹高的回報 (5–9%)" },
      { key: "C", label: "我願意承擔更多的風險(可能下跌30%)，來換取更高的回報 (10–12%)" },
      { key: "D", label: "我願意承擔巨大的風險(可能下跌超過30%)，來換取更高的回報 (>12%)" },
    ],
  },
  {
    key: "marketDownturnReaction",
    question: "marketDownturnReaction",
    layout: "stack",
    label: "6. 假設您在兩年前作了一項投資(當時計劃投資10年)，現在正值跌市，投資價值比當時下跌了 25%，您會如何反應？",
    requiredMessage: "請選擇第6題：假設您在兩年前作了一項投資(當時計劃投資10年)，現在正值跌市，投資價值比當時下跌了25%，您會如何反應？",
    options: [
      { key: "A", label: "不作考慮下取消所有投資並取回剩餘款項" },
      { key: "B", label: "不作考慮下等待股市回升或增加投資" },
      { key: "C", label: "分析後取消所有投資並取回剩餘款項" },
      { key: "D", label: "分析後等待股市回升或增加投資" },
    ],
  },
  {
    key: "acceptableInvestmentLoss",
    question: "acceptableInvestmentLoss",
    layout: "grid-2",
    label: "7. 投資難免涉及風險，價格可能大幅波動，您願意承受多少的虧損？",
    requiredMessage: "請選擇第7題：投資難免涉及風險，價格可能大幅波動，您願意承受多少的虧損？",
    options: [
      { key: "A", label: "< 10%" },
      { key: "B", label: "10% – 20%" },
      { key: "C", label: "21% - 35%" },
      { key: "D", label: "> 35%" },
    ],
  },
];

function getSelectedCheckboxKeys(question: FormData | undefined) {
  return (question?.items || []).filter((item) => item?.value === true).map((item) => item?.key);
}

function getSelectedRadioKey(question: FormData | undefined) {
  return (question?.items || []).find((item) => item?.value === true)?.key;
}

export function shouldRequireZuuRpq(financialAnalysis?: FormData[]) {
  const objectiveQuestion = (financialAnalysis || []).find((question) => question?.question === "insurancePurchaseObjectives");
  const investmentQuestion = (financialAnalysis || []).find((question) => question?.question === "insurancePurchaseObjectives_E");
  const selectedObjectives = getSelectedCheckboxKeys(objectiveQuestion);
  const selectedInvestmentPreference = getSelectedRadioKey(investmentQuestion);
  return selectedObjectives.includes("E") && selectedInvestmentPreference === "A";
}

export function isZuuRpqComplete(value?: ZuuRiskInvestmentProfile | null) {
  if (!value) return false;
  return ZUU_RPQ_REQUIRED_FIELDS.every((field) => !!value[field]);
}

export function mapZuuRpqToFormValue(rpq?: FormData[] | null): ZuuRiskInvestmentProfile {
  const questionMap = new Map((rpq || []).filter((item) => item?.question).map((item) => [item.question, item]));
  return ZUU_RPQ_QUESTIONS.reduce<ZuuRiskInvestmentProfile>((acc, question) => {
    const selected = getSelectedRadioKey(questionMap.get(question.question));
    if (selected) {
      acc[question.key] = selected as never;
    }
    return acc;
  }, {});
}

export function mapZuuRpqToPayload(value?: ZuuRiskInvestmentProfile | null): FormData[] {
  if (!value) return [];
  return ZUU_RPQ_QUESTIONS.flatMap((question) => {
    const selected = value[question.key];
    if (!selected) return [];
    return [{
      question: question.question,
      questionNumber: null,
      type: "RADIO",
      items: [{ key: selected, value: true }],
    } satisfies FormData];
  });
}

export function shouldShowZuuRpqPdf(financial?: Pick<OrderFinancialVO, "financialAnalysis" | "rpq"> | null) {
  return shouldRequireZuuRpq(financial?.financialAnalysis) && isZuuRpqComplete(mapZuuRpqToFormValue(financial?.rpq));
}

export function isZuuRpqDocument(doc: { formType?: string; type?: string; key?: string; name?: string } | null | undefined) {
  const marker = [doc?.formType, doc?.type, doc?.key, doc?.name].filter(Boolean).join(" ").toUpperCase();
  return marker.includes("RPQ") || marker.includes("RISK_INVESTMENT");
}
